import { useState } from 'react'
import useApiService from 'services/api'
import { Tag } from 'models/tags'
import BaseSelectModal, {
  BaseSelectModalProps,
} from './BaseSelectModal/BaseSelectModal'
import { bulkEditSingleParams } from 'services/apiTypes'
import { bulkUpdateDealReportsSingleField } from 'services/reports'
import { useGatedStageMap } from 'services/captureForms'
import type { CaptureForm } from 'models/captureForms'
import { CaptureFormModal } from 'components/CaptureForms/CaptureFormModal'

export default function SalesStageSelectModal({
  tableObjAccessor = 'sales_stage',
  useReportsEndpoint = false,
  dealId,
  handleClose,
  ...props
}: {
  tableObjAccessor?: string
  useReportsEndpoint?: boolean
  /**
   * When provided (single-deal context), stage moves into gated stages will
   * open the capture form instead of performing the direct move. For bulk
   * table actions (no dealId), gating is skipped and behavior is unchanged.
   */
  dealId?: number
} & Omit<
  BaseSelectModalProps<Tag>,
  | 'apiAction'
  | 'title'
  | 'subtitle'
  | 'label'
  | 'placeholder'
  | 'onConfirmAddNew'
  | 'items'
  | 'tableObjAccessor'
>) {
  const api = useApiService()
  const { data: salesStages } = api.useGetSalesStageOptions(true)

  // Shared gating map (SalesStage id → gating CaptureForm). Only consulted when
  // a dealId is present; for bulk actions the direct move runs and the server
  // is the backstop that rejects an uncaptured move into a gated stage.
  // `gatingLoading` is treated as "gating unknown": until forms resolve, the
  // map is empty, so a single-deal move is refused rather than slipping past
  // the capture modal into a direct (server-rejected) move.
  const { gatedStageMap, isLoading: gatingLoading } = useGatedStageMap()

  // When a gated stage is selected, stash it here; renders the CaptureFormModal.
  const [pendingCapture, setPendingCapture] = useState<{
    stageId: number
    form: CaptureForm
  } | null>(null)

  const apiAction = async (params: bulkEditSingleParams) => {
    const { item_id: sales_stage, ...rest } = params
    const targetStageId =
      typeof sales_stage === 'number' ? sales_stage : Number(sales_stage)

    // Guard: only intercept when we have a single dealId. For non-gated stages
    // (or bulk actions without dealId), behavior is unchanged.
    if (dealId && sales_stage !== null) {
      // Gating not yet known: refuse the move until forms resolve, so a fast
      // click right after load can't skip an as-yet-unloaded capture modal.
      if (gatingLoading) return undefined
      if (gatedStageMap.has(targetStageId)) {
        const form = gatedStageMap.get(targetStageId)!
        setPendingCapture({ stageId: targetStageId, form })
        // Close the stage-select modal; the capture modal opens in its place.
        handleClose()
        // Return undefined so BaseSelectModal rolls back the optimistic update
        // without showing a success toast. The actual stage move will happen
        // inside CaptureFormModal.handleSubmit via useSubmitCapture.
        return undefined
      }
    }

    // Non-gated path — completely unchanged.
    if (useReportsEndpoint) {
      return bulkUpdateDealReportsSingleField(
        { sales_stage_id: sales_stage },
        rest
      )
    }
    return api.bulkEditDeal({
      deal: { sales_stage_id: sales_stage },
      ...rest,
    })
  }

  if (!salesStages) return null

  return (
    <>
      <BaseSelectModal
        {...props}
        handleClose={handleClose}
        title="Change Sales Stage"
        subtitle={
          <span>
            Add or replace the sales stage for <b>all</b> selected rows.
          </span>
        }
        label="Sales Stage"
        placeholder="Select Sales Stage"
        items={salesStages}
        tableObjAccessor={tableObjAccessor!}
        apiAction={apiAction}
      />

      {/* Capture form modal — rendered only when a gated stage was selected. */}
      {pendingCapture && dealId && (
        <CaptureFormModal
          form={pendingCapture.form}
          dealId={dealId}
          targetStageId={pendingCapture.stageId}
          isOpen
          onClose={() => setPendingCapture(null)}
          onSuccess={() => setPendingCapture(null)}
        />
      )}
    </>
  )
}
